<template>
    <div class="slider-component">
        <div :ref="'elem' + id" aria-hidden="true" class="slider" :style="{ background: bg }">
            <div
                v-show="!disabled"
                :ref="'dot0' + id"
                :class="['slider-dot', flag ? 'slider-always' : 'slider-hover']"
                @mousedown="moveStart($event, 0)"
            >
                <span class="slider-tooltip-wrap" :index="0">{{ targetToMMSS }}</span>
            </div>
            <div
                v-show="!disabled"
                :ref="'dot1' + id"
                :class="['slider-dot', flag ? 'slider-always' : 'slider-hover']"
                @mousedown="moveStart($event, 1)"
            >
                <span class="slider-tooltip-wrap" :index="1">{{ delayedToMMSS }}</span>
            </div>
            <div v-show="!disabled" :ref="'process' + id" class="slider-process"></div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { secondsToMMSS } from '../common/helpers';

export default {
    name: 'Slider',

    props: {
        max: {
            type: Number,
            default: 100,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        value: {
            type: Array,
            default: () => [0, 0],
        },

        id: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            flag: false,
            size: 0,
            currentValue: [0, 0],
            currentSlider: 0,
        };
    },

    computed: {
        ...mapState(['navCollapsed']),

        /**
         * Links to both slider's dots
         * @returns {Array}
         */
        slider() {
            return [this.$refs['dot0' + this.id], this.$refs['dot1' + this.id]];
        },

        gap() {
            return this.size / this.max;
        },

        /**
         * Finds actual position for both dots
         * @returns {Array}
         */
        position() {
            return [this.currentValue[0] * this.gap, this.currentValue[1] * this.gap];
        },

        /**
         * Finds position limits for both dots
         * @returns {Array}
         */
        limit() {
            return [
                [0, this.position[1]],
                [this.position[0], this.size],
            ];
        },

        /**
         * Finds value limits for both dots
         * @returns {Array}
         */
        valueLimit() {
            return [
                [0, this.currentValue[1]],
                [this.currentValue[0], this.max],
            ];
        },

        /**
         * Calculates slider's background gradient for $on-time-color/$late-color separation
         * @returns {String}
         */
        bg() {
            if (this.disabled) {
                return '#4baf4f';
            }

            let greenEnd = Math.round(this.value[0] / (this.max / 100));
            return (
                'linear-gradient(90deg, #4baf4f, #4baf4f ' +
                greenEnd +
                '%, #f44236 ' +
                greenEnd +
                '%, #f44236)'
            );
        },

        /**
         * Converts seconds to mm:ss in the first dot's tooltip
         * @returns {string}
         */
        targetToMMSS() {
            return secondsToMMSS(this.value[0]);
        },

        /**
         * Converts seconds to mm:ss in the second dot's tooltip
         * @returns {string}
         */
        delayedToMMSS() {
            return secondsToMMSS(this.value[1]);
        },
    },

    watch: {
        value(val) {
            if (!this.flag) {
                this.setValue(val);
            }
        },

        navCollapsed() {
            this.refresh();
        },
    },

    mounted() {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        this.$nextTick(() => {
            this.getStaticData();
            this.setValue(this.value);
            this.bindEvents();
        });
    },

    beforeDestroy() {
        this.unbindEvents();
    },

    methods: {
        bindEvents() {
            document.addEventListener('mousemove', this.moving);
            document.addEventListener('mouseup', this.moveEnd);
            document.addEventListener('mouseleave', this.moveEnd);

            window.addEventListener('resize', this.refresh);
        },

        unbindEvents() {
            window.removeEventListener('resize', this.refresh);

            document.removeEventListener('mousemove', this.moving);
            document.removeEventListener('mouseup', this.moveEnd);
            document.removeEventListener('mouseleave', this.moveEnd);
        },

        getPos(e) {
            this.getStaticData();
            return e.clientX - this.offset;
        },

        moveStart(e, index) {
            this.currentSlider = index;
            this.flag = true;
            this.$emit('drag-start', this);
        },

        moving(e) {
            if (!this.flag) {
                return false;
            }
            e.preventDefault();

            if (e.targetTouches && e.targetTouches[0]) {
                e = e.targetTouches[0];
            }
            this.setValueOnPos(this.getPos(e), true);
        },

        moveEnd() {
            if (this.flag) {
                this.$emit('drag-end', this);
            } else {
                return false;
            }
            this.flag = false;
            this.setPosition();
        },

        setValueOnPos(pos, isDrag) {
            let range = this.limit[this.currentSlider];
            let valueRange = this.valueLimit[this.currentSlider];
            if (pos >= range[0] && pos <= range[1]) {
                this.setTransform(pos);
                let v = Math.round(pos / this.gap);
                this.setCurrentValue(v, isDrag);
            } else if (pos < range[0]) {
                this.setTransform(range[0]);
                this.setCurrentValue(valueRange[0]);
                if (this.currentSlider === 1) {
                    this.currentSlider = 0;
                }
            } else {
                this.setTransform(range[1]);
                this.setCurrentValue(valueRange[1]);
                if (this.currentSlider === 0) {
                    this.currentSlider = 1;
                }
            }
        },

        /**
         * Compares passed parameters
         * @param a
         * @param b
         */
        isDiff(a, b) {
            if (Object.prototype.toString.call(a) !== Object.prototype.toString.call(b)) {
                return true;
            } else if (Array.isArray(a) && a.length === b.length) {
                return a.some((v, i) => v !== b[i]);
            }
            return a !== b;
        },

        /**
         * Sets currentValue on drag event
         * @param val
         * @param bool
         */
        setCurrentValue(val, bool) {
            if (val < 0 || val > this.max) return false;
            if (this.isDiff(this.currentValue[this.currentSlider], val)) {
                this.currentValue.splice(this.currentSlider, 1, val);
                this.syncValue();
            }
            if (!bool) {
                this.setPosition();
            }
        },

        /**
         * Sets currentValue on prop value change
         * @param val
         */
        setValue(val) {
            if (this.isDiff(this.currentValue, val)) {
                this.currentValue = val.concat();
                this.syncValue();
            }

            this.$nextTick(() => this.setPosition());
        },

        setPosition() {
            this.currentSlider = 0;
            this.setTransform(this.position[this.currentSlider]);
            this.currentSlider = 1;
            this.setTransform(this.position[this.currentSlider]);
        },

        /**
         * Sets inline styles for dots' and gap's render
         * @param val
         */
        setTransform(val) {
            let translateValue = `translateX(${val - 11}px)`; // dotSize / 2 = 11
            let processSize = `${
                this.currentSlider === 0 ? this.position[1] - val : val - this.position[0]
            }px`;
            let processPos = `${this.currentSlider === 0 ? val : this.position[0]}px`;

            this.slider[this.currentSlider].style.transform = translateValue;
            this.slider[this.currentSlider].style.WebkitTransform = translateValue;
            this.slider[this.currentSlider].style.msTransform = translateValue;

            this.$refs['process' + this.id].style.width = processSize;
            this.$refs['process' + this.id].style.left = processPos;
        },

        /**
         * Updates value property
         */
        syncValue() {
            this.$emit('input', this.currentValue.concat());
        },

        /**
         * Gets rendered component sizes for proper dots positioning
         */
        getStaticData() {
            if (this.$refs['elem' + this.id]) {
                this.size = this.$refs['elem' + this.id].offsetWidth;
                this.offset = this.$refs['elem' + this.id].getBoundingClientRect().left;
            }
        },

        /**
         * Refreshes (rerenders) component
         */
        refresh() {
            if (this.$refs['elem' + this.id]) {
                this.getStaticData();
                this.setPosition();
            }
        },
    },
};
</script>
